// PropTypes definitions for consistent component validation
// This provides runtime type checking for component props

import { StatusTypes, SeverityTypes, SizeVariants, ThemeVariants } from '../types/common';

// Create custom PropTypes validators
export const createEnumValidator = (enumObject, enumName) => {
  const validValues = Object.values(enumObject);
  
  return (props, propName, componentName) => {
    const value = props[propName];
    
    if (value === undefined) {
      return null; // Allow undefined values
    }
    
    if (!validValues.includes(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`, ` +
        `expected one of: ${validValues.join(', ')}`
      );
    }
    
    return null;
  };
};

// Pre-built validators for common enums
export const statusPropType = createEnumValidator(StatusTypes, 'StatusTypes');
export const severityPropType = createEnumValidator(SeverityTypes, 'SeverityTypes'); 
export const sizePropType = createEnumValidator(SizeVariants, 'SizeVariants');
export const themePropType = createEnumValidator(ThemeVariants, 'ThemeVariants');

// Common prop type shapes for reuse
export const commonPropTypes = {
  // Base component props
  className: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && typeof value !== 'string') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`string\``
      );
    }
  },
  
  // Children prop
  children: (props, propName, componentName) => {
    // Children can be anything renderable by React
    return null;
  },
  
  // Event handlers
  onClick: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && typeof value !== 'function') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`function\``
      );
    }
  },
  
  // Loading state
  loading: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && typeof value !== 'boolean') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`boolean\``
      );
    }
  },
  
  // Disabled state
  disabled: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && typeof value !== 'boolean') {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`boolean\``
      );
    }
  }
};

// Compound prop type creators
export const createArrayOfValidator = (itemValidator) => {
  return (props, propName, componentName) => {
    const value = props[propName];
    
    if (value === undefined) {
      return null;
    }
    
    if (!Array.isArray(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`array\``
      );
    }
    
    // Validate each item in the array
    for (let i = 0; i < value.length; i++) {
      const error = itemValidator({ [propName]: value[i] }, propName, componentName);
      if (error) {
        return new Error(
          `Invalid prop \`${propName}[${i}]\` ${error.message.split(' ').slice(4).join(' ')}`
        );
      }
    }
    
    return null;
  };
};

export const createShapeValidator = (shape) => {
  return (props, propName, componentName) => {
    const value = props[propName];
    
    if (value === undefined) {
      return null;
    }
    
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`object\``
      );
    }
    
    // Validate each property in the shape
    for (const [key, validator] of Object.entries(shape)) {
      if (typeof validator === 'function') {
        const error = validator({ [key]: value[key] }, key, componentName);
        if (error) {
          return new Error(
            `Invalid prop \`${propName}.${key}\` ${error.message.split(' ').slice(4).join(' ')}`
          );
        }
      }
    }
    
    return null;
  };
};

// Common shape validators
export const standardPropType = createShapeValidator({
  id: (props, propName) => {
    const value = props[propName];
    if (typeof value !== 'string') {
      return new Error(`Expected string, got ${typeof value}`);
    }
  },
  name: (props, propName) => {
    const value = props[propName];
    if (typeof value !== 'string') {
      return new Error(`Expected string, got ${typeof value}`);
    }
  },
  percentage: (props, propName) => {
    const value = props[propName];
    if (typeof value !== 'number' || value < 0 || value > 100) {
      return new Error(`Expected number between 0-100, got ${value}`);
    }
  }
});

export const vulnerabilityPropType = createShapeValidator({
  id: (props, propName) => {
    const value = props[propName];
    if (typeof value !== 'string' && typeof value !== 'number') {
      return new Error(`Expected string or number, got ${typeof value}`);
    }
  },
  title: (props, propName) => {
    const value = props[propName];
    if (typeof value !== 'string') {
      return new Error(`Expected string, got ${typeof value}`);
    }
  },
  severity: severityPropType,
  status: statusPropType
});

// Helper for required props
export const isRequired = (validator) => {
  const requiredValidator = (props, propName, componentName) => {
    const value = props[propName];
    
    if (value === undefined || value === null) {
      return new Error(
        `The prop \`${propName}\` is marked as required in \`${componentName}\`, but its value is \`${value}\``
      );
    }
    
    return validator(props, propName, componentName);
  };
  
  requiredValidator.isRequired = requiredValidator;
  return requiredValidator;
};

// Export a PropTypes-like object for easy usage
export const PropTypes = {
  string: commonPropTypes.className,
  bool: commonPropTypes.loading,
  func: commonPropTypes.onClick,
  node: commonPropTypes.children,
  number: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && (typeof value !== 'number' || Number.isNaN(value))) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`number\``
      );
    }
  },
  array: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && !Array.isArray(value)) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`array\``
      );
    }
  },
  object: (props, propName, componentName) => {
    const value = props[propName];
    if (value !== undefined && (typeof value !== 'object' || value === null || Array.isArray(value))) {
      return new Error(
        `Invalid prop \`${propName}\` of type \`${typeof value}\` supplied to \`${componentName}\`, expected \`object\``
      );
    }
  },
  
  // Custom validators
  status: statusPropType,
  severity: severityPropType,
  size: sizePropType,
  theme: themePropType,
  
  // Shape validators
  standard: standardPropType,
  vulnerability: vulnerabilityPropType,
  
  // Utility functions
  arrayOf: createArrayOfValidator,
  shape: createShapeValidator,
  oneOf: createEnumValidator
};

// Make all validators optionally required
Object.keys(PropTypes).forEach(key => {
  if (typeof PropTypes[key] === 'function') {
    PropTypes[key].isRequired = isRequired(PropTypes[key]);
  }
});